import { KeyValuePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateDirective } from '@ngx-translate/core';

import { TranslatePipe } from '../../pipes/translate.pipe';
import { DropdownComponent } from '../../components/dropdown/dropdown.component';
import { DropdownItemComponent } from '../../components/dropdown/dropdown-item.component';

import { FormRowComponent } from '../../components/form/row/form-row.component';
import { InputDirective } from '../../components/input/input.directive';
import { InputErrorsComponent } from '../../components/form/input-errors/input-errors.component';
import { InputStatusIndicatorDirective } from '../../components/form/input-status/input-status-indicator.directive';
import { InputGroupComponent } from '../../components/form/input-group/input-group.component';
import { InputGroupLabelComponent } from '../../components/form/input-group/input-group-label.component';
import { InputGroupFieldComponent } from '../../components/form/input-group/input-group-field.component';
import { RuralRoutePoBoxInputDirective } from '../../components/input/rural-route-po-box-input.directive';

import { CallOutComponent } from '../call-out/call-out.component';
import { PostalCodeValidatorDirective } from '../postal-code/postal-code-validator.directive';

import { Address } from '../../domain/address.domain';
import { AddressService } from './address.service';

@Component({
  selector: 'td-wb-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss', './address-autocomplete.scss'],
  providers: [AddressService],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CallOutComponent,
    TranslatePipe,
    FormRowComponent,
    InputDirective,
    InputErrorsComponent,
    InputGroupComponent,
    InputGroupLabelComponent,
    InputGroupFieldComponent,
    InputStatusIndicatorDirective,
    FormsModule,
    DropdownComponent,
    DropdownItemComponent,
    KeyValuePipe,
    PostalCodeValidatorDirective,
    CallOutComponent,
    TranslateDirective,
    RuralRoutePoBoxInputDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddressComponent implements OnInit {

  @Input() disabled: boolean;
  @Input() usageId: string = 'home';
  @Input() showTitleAndCallout: boolean = true;
  @Input() showRuralRouteAndPOBox: boolean = true;

  public streetNumDisplay: string = '';
  public ruralOrPoBox: string = '';
  public ruralOrPoBoxType: string = '';
  public station: string;
  public selectedAddress: Address;

  constructor(private addressService: AddressService,
    private changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.addressService.loadAddressComplete(this.usageId);
    this.addressService.onSelectedAddressChange.subscribe((selectedAddress: Address) => {
      this.selectedAddress = selectedAddress;
      this.populateRuralRouteAndPoBoxInfo();
      this.changeDetectorRef.markForCheck();
    });
  }

  getProvinces(): Map<string, string> {
    return this.addressService.provinces;
  }

  getRuralPoTypes(): Array<string> {
    return this.addressService.RURAL_PO_TYPES;
  }

  getSelectedAddress(): Address {

    // populate steet number suffix if it has it
    this.selectedAddress.streetNumSuffixVal = this.addressService.parseStreetNumberSuffix(this.streetNumDisplay);
    this.selectedAddress.streetNum = this.addressService.parseStreetNumber(this.streetNumDisplay);

    if (this.usageId === 'home' && this.ruralOrPoBox) {
      this.ruralOrPoBoxType = AddressService.RURAL_ROUTE_TYPE;
    }

    if (this.showRuralRouteAndPOBox) {
      this.selectedAddress.ruralRouteTypeCd = this.ruralOrPoBoxType;
      if (this.ruralOrPoBoxType === AddressService.PO_BOX_TYPE) {
        this.selectedAddress.poBox = this.ruralOrPoBox;
        this.selectedAddress.ruralRouteNum = '';
      } else {
        this.selectedAddress.ruralRouteNum = this.ruralOrPoBox;
        this.selectedAddress.poBox = '';
      }
    }

    // in case province was populated manually
    this.selectedAddress.territoryCd = this.selectedAddress.territory;
    // Ensure default country is set for manual entry
    if (!this.selectedAddress.countryCd) {
      this.selectedAddress.countryCd = 'CA';
    }
    if (!this.selectedAddress.country) {
      this.selectedAddress.country = 'Canada';
    }
    return this.selectedAddress;
  }

  getRuralOrPoBoxType(): string {
    return this.ruralOrPoBoxType;
  }

  setSelectedAddress(address: Address): void {
    this.selectedAddress = address;
    this.populateRuralRouteAndPoBoxInfo();
  }

  isRuralOrPoBoxEmpty(): boolean {
    return this.ruralOrPoBox === '' || this.ruralOrPoBox === null || this.ruralOrPoBox === undefined;
  }

  shouldDisableStation(): boolean {
    return this.disablePOBoxRuralRoute() || this.isRuralOrPoBoxEmpty();
  }

  onRuralOrPoB0xChange(): void {
    if (this.isRuralOrPoBoxEmpty()) {
      this.selectedAddress.postalStationTxt = '';
    }
  }

  disablePOBoxRuralRoute(): boolean {
    if ((this.usageId === 'home') || (this.usageId === 'mailing' && this.addressService.RURAL_PO_TYPES.some(
      (aType: string) => aType === this.ruralOrPoBoxType))) {
      return false;
    }

    return true;
  }

  isPoBoxRuralRouteRequired(): boolean {
    if (this.usageId !== 'home' && this.addressService.RURAL_PO_TYPES.some(
      (aType: string) => aType === this.ruralOrPoBoxType)) {
      return true;
    }
    return false;
  }

  private populateRuralRouteAndPoBoxInfo(): void {
    this.streetNumDisplay = [this.selectedAddress.streetNum, this.selectedAddress?.streetNumSuffixVal].join('');

    if (this.selectedAddress.ruralRouteNum && this.showRuralRouteAndPOBox) {
      this.ruralOrPoBox = this.selectedAddress.ruralRouteNum;
      this.ruralOrPoBoxType = AddressService.RURAL_ROUTE_TYPE;
    } else if (this.selectedAddress.poBox && this.showRuralRouteAndPOBox) {
      this.ruralOrPoBox = this.selectedAddress.poBox;
      this.ruralOrPoBoxType = AddressService.PO_BOX_TYPE;
    } else {
      this.ruralOrPoBox = '';
      this.ruralOrPoBoxType = '';
    }
  }
}
